import { useMemo } from 'react'
import { differenceInMilliseconds, parseISOToDate } from '@chilipiper/date-time'
import { PublicMeeting } from '@chilipiper/api-client/src/meetings'
import { useMeetingSettings } from '../api-queries'

const DEFAULT_MEETING_DURATION_IN_MS = 30 * 60 * 1000 // 30 minutes

const getMeetingDurationInMsString = (meetingStart: Date, meetingEnd: Date) => {
  const durationInMinutes = differenceInMilliseconds(meetingEnd, meetingStart)

  return `${durationInMinutes} milliseconds`
}

export const useMeetingTypeOverride = ({
  tenantId,
  meetingDetails,
  meetingId,
}: {
  tenantId: string
  meetingDetails?: PublicMeeting
  meetingId?: string
}) => {
  const { data: meetingSettings } = useMeetingSettings({
    tenantId,
    meetingId,
  })

  const meetingDurationOverride = useMemo(() => {
    if (!meetingDetails) {
      return `${DEFAULT_MEETING_DURATION_IN_MS} milliseconds`
    }
    return getMeetingDurationInMsString(
      parseISOToDate(meetingDetails.dateTime.start),
      parseISOToDate(meetingDetails.dateTime.end)
    )
  }, [meetingSettings])

  const beforeBufferDurationOverride = useMemo(() => {
    if (meetingSettings?.meetingType.buffers.type == 'Before') {
      return meetingSettings?.meetingType.buffers.duration
    }
    if (meetingSettings?.meetingType.buffers.type == 'BeforeAndAfter') {
      return meetingSettings?.meetingType.buffers.before
    }
    return undefined
  }, [meetingSettings])

  const afterBufferDurationOverride = useMemo(() => {
    if (meetingSettings?.meetingType.buffers.type == 'After') {
      return meetingSettings?.meetingType.buffers.duration
    }
    if (meetingSettings?.meetingType.buffers.type == 'BeforeAndAfter') {
      return meetingSettings?.meetingType.buffers.after
    }
    return undefined
  }, [meetingSettings])

  const meetingTypeOverride = useMemo(() => {
    return {
      duration: meetingDurationOverride,
      ...(beforeBufferDurationOverride
        ? { beforeBufferDuration: beforeBufferDurationOverride }
        : {}),
      ...(afterBufferDurationOverride ? { afterBufferDuration: afterBufferDurationOverride } : {}),
    }
  }, [meetingDurationOverride, beforeBufferDurationOverride, afterBufferDurationOverride])

  return meetingTypeOverride
}
